import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUp,
  BookOpen,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  Copy,
  Check,
  Headphones,
  HeartHandshake,
  HelpCircle,
  LayoutDashboard,
  MessageCircle,
  Search,
  Sparkles,
  Wallet,
  X,
} from 'lucide-react';
import { FAQ_CATEGORIES, FAQ_ITEMS, type FaqCategoryId } from '../data/faqCenterData';

const CATEGORY_ICON: Record<FaqCategoryId, React.ElementType> = {
  family_relations: HeartHandshake,
  inner_growth: Sparkles,
  work_money: Wallet,
  concepts: HelpCircle,
  audio_packages: Headphones,
  account_tech: LayoutDashboard,
};

function normalizeFaqSearch(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[\u064a\u06cc]/g, '\u06cc')
    .replace(/[\u0643\u06a9]/g, '\u06a9')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ی')
    .replace(/[\u200c\u200f\u202a-\u202e]/g, '');
}

function matchesSearch(
  item: (typeof FAQ_ITEMS)[number],
  normalizedQuery: string,
): boolean {
  if (!normalizedQuery || normalizedQuery.length < 2) return true;
  const catLabel = FAQ_CATEGORIES.find((c) => c.id === item.categoryId)?.label ?? '';
  const blob = normalizeFaqSearch(
    [item.question, item.answer, ...(item.keywords ?? []), catLabel].join('\n'),
  );
  const tokens = normalizedQuery.split(' ').filter((t) => t.length >= 1);
  if (tokens.length === 0) return true;
  return tokens.every((t) => blob.includes(t));
}

function faqHashId(raw: string): string | null {
  const h = raw.replace(/^#/, '');
  return h.startsWith('faq-') ? h : null;
}

const FAQPage: React.FC = () => {
  const location = useLocation();
  const [rawQuery, setRawQuery] = useState('');
  const deferredQuery = useDeferredValue(rawQuery);
  const [activeCategory, setActiveCategory] = useState<FaqCategoryId | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(FAQ_ITEMS[0]?.id ?? null);
  const [expandAll, setExpandAll] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [stickyBarElevated, setStickyBarElevated] = useState(false);

  const searchRef = useRef<HTMLInputElement | null>(null);
  const listAnchorRef = useRef<HTMLDivElement | null>(null);
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);

  const normalizedDeferred = useMemo(() => normalizeFaqSearch(deferredQuery), [deferredQuery]);

  const filteredItems = useMemo(() => {
    const base =
      activeCategory === 'all' ? FAQ_ITEMS : FAQ_ITEMS.filter((i) => i.categoryId === activeCategory);
    return base.filter((item) => matchesSearch(item, normalizedDeferred));
  }, [activeCategory, normalizedDeferred]);

  const countsByCategory = useMemo(() => {
    const map = new Map<FaqCategoryId, number>();
    for (const c of FAQ_CATEGORIES) map.set(c.id, 0);
    for (const item of FAQ_ITEMS) {
      map.set(item.categoryId, (map.get(item.categoryId) ?? 0) + 1);
    }
    return map;
  }, []);

  const isSearchPending = rawQuery !== deferredQuery;

  const isItemOpen = useCallback(
    (id: string) => expandAll || openId === id,
    [expandAll, openId],
  );

  const toggleOpen = useCallback((id: string) => {
    if (expandAll) {
      setExpandAll(false);
      setOpenId((prev) => (prev === id ? null : id));
      return;
    }
    setOpenId((prev) => (prev === id ? null : id));
  }, [expandAll]);

  const handleExpandAll = useCallback(() => {
    setExpandAll(true);
    setOpenId(null);
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandAll(false);
    setOpenId(null);
  }, []);

  const scrollToList = useCallback(() => {
    listAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCategoryClick = useCallback(
    (cat: FaqCategoryId | 'all') => {
      setActiveCategory(cat);
      setTimeout(scrollToList, 80);
    },
    [scrollToList],
  );

  const copyItemLink = useCallback(async (id: string) => {
    const url = `${window.location.origin}${location.pathname}#faq-${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setCopiedId(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape' && rawQuery) {
        setRawQuery('');
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rawQuery]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 380);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = stickySentinelRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([e]) => setStickyBarElevated(!e.isIntersecting),
      { rootMargin: '-1px 0px 0px 0px', threshold: 1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const hashRaw = location.hash || window.location.hash;
    const hashId = faqHashId(hashRaw);
    if (!hashId) return;
    const id = hashId.replace(/^faq-/, '');
    const exists = FAQ_ITEMS.some((i) => i.id === id);
    if (!exists) return;
    setExpandAll(false);
    setOpenId(id);
    window.requestAnimationFrame(() => {
      document.getElementById(hashId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [location.hash]);

  useEffect(() => {
    if (expandAll) return;
    if (openId && !filteredItems.some((i) => i.id === openId)) {
      setOpenId(filteredItems[0]?.id ?? null);
    }
  }, [expandAll, filteredItems, openId]);

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-[#f8fafc] via-[#eef2ff]/85 to-[#f8fafc] pb-24 pt-6 text-[#0f172a] sm:pt-10">
      <div ref={stickySentinelRef} className="pointer-events-none h-px w-full opacity-0" aria-hidden />

      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-5xl lg:px-8">
        <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <Link to="/" className="font-semibold text-[#1C3B9B] transition hover:text-[#7c3aed]">
            خانه
          </Link>
          <span aria-hidden className="text-slate-300">
            /
          </span>
          <span className="font-bold text-slate-700">سوالات متداول</span>
        </nav>

        <header className="relative overflow-hidden rounded-[1.75rem] border border-slate-200/90 bg-white p-6 shadow-[0_28px_60px_-36px_rgba(28,59,155,0.5)] sm:rounded-3xl sm:p-8 lg:p-10">
          <div className="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-[#7c3aed]/14 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 -right-16 h-72 w-72 rounded-full bg-[#1C3B9B]/12 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(28,59,155,0.04)_0%,transparent_42%,rgba(124,58,237,0.05)_100%)]" />

          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-10">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-[#1C3B9B]/15 bg-[#1C3B9B]/[0.06] px-3 py-1 text-[11px] font-bold tracking-wide text-[#1C3B9B] sm:text-xs">
                <BookOpen className="h-3.5 w-3.5" aria-hidden />
                مرکز راهنما
              </p>
              <h1 className="mt-3 text-2xl font-black leading-[1.2] text-[#0f172a] sm:text-4xl lg:text-[2.35rem] lg:leading-tight">
                سوالات{' '}
                <span className="bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] bg-clip-text text-transparent">متداول</span>
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-8">
                پاسخ‌ها جنبهٔ آموزشی دارند و جایگزین مشاورهٔ فردی، روان‌درمانی، مداخلهٔ اورژانسی یا مشاورهٔ حقوقی نیستند. برای موارد حاد با متخصصان معتبر تماس بگیرید.
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="inline-flex items-center gap-2 rounded-2xl border border-amber-200/90 bg-gradient-to-l from-amber-50 to-amber-50/40 px-3.5 py-2.5 text-[11px] font-semibold leading-5 text-amber-950 sm:max-w-md sm:text-xs">
                  <MessageCircle className="h-4 w-4 shrink-0 text-amber-700" aria-hidden />
                  <span>پرسش اختصاصی دارید؟ تیم پشتیبانی پاسخ‌گوست.</span>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] px-5 text-sm font-bold text-white shadow-[0_14px_28px_-12px_rgba(28,59,155,0.55)] transition hover:brightness-[1.03] active:scale-[0.99]"
                >
                  تماس با پشتیبانی
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { n: FAQ_ITEMS.length, l: 'پرسش', sub: 'پوشش موضوعی' },
                { n: FAQ_CATEGORIES.length, l: 'دسته', sub: 'فیلتر سریع' },
                { n: '۱۲+', l: 'کلید میانبر', sub: 'جستجو با /' },
              ].map((s, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 p-3 text-center shadow-sm sm:p-4"
                >
                  <p className="text-lg font-black tabular-nums text-[#1C3B9B] sm:text-xl">{s.n}</p>
                  <p className="mt-0.5 text-[10px] font-bold text-slate-800 sm:text-xs">{s.l}</p>
                  <p className="mt-1 text-[9px] font-medium text-slate-500 sm:text-[10px]">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </header>

        <div className="sticky top-[calc(var(--site-header-height,88px)+8px)] z-30 mt-8">
          <div
            className={`space-y-4 rounded-2xl border border-slate-200 bg-white p-4 transition-shadow duration-300 sm:p-5 ${
              stickyBarElevated
                ? 'shadow-[0_10px_28px_-8px_rgba(15,23,42,0.12),0_2px_8px_-4px_rgba(15,23,42,0.06)]'
                : 'shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
            }`}
          >
            <label htmlFor="faq-search" className="sr-only">
              جستجو در سوالات و پاسخ‌ها
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute right-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden />
              <input
                ref={searchRef}
                id="faq-search"
                type="search"
                autoComplete="off"
                placeholder="جستجو در سوال، پاسخ و موضوعات…"
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-24 pr-11 text-sm font-medium text-[#0f172a] outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-[#1C3B9B] focus:bg-white focus:ring-2 focus:ring-[#1C3B9B]/15"
              />
              <div className="pointer-events-none absolute left-3 top-1/2 hidden -translate-y-1/2 sm:block">
                <kbd className="rounded-lg border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-500 shadow-sm">Esc</kbd>
                <span className="mr-1.5 text-[10px] text-slate-400">پاک کردن</span>
              </div>
              {rawQuery ? (
                <button
                  type="button"
                  onClick={() => setRawQuery('')}
                  className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-[#1C3B9B] sm:left-[4.5rem]"
                  aria-label="پاک کردن جستجو"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-center text-[10px] text-slate-500 sm:text-right sm:text-xs">
                <span className="font-semibold text-slate-600">میانبر:</span>{' '}
                <kbd className="rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px]">/</kbd>
                {' ← '}فوکوس جستجو
                {isSearchPending ? <span className="mr-2 font-bold text-[#1C3B9B]">· در حال جستجو…</span> : null}
              </p>
              <div className="flex items-center justify-center gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={handleExpandAll}
                  disabled={filteredItems.length === 0}
                  className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 text-[11px] font-bold text-slate-700 transition hover:border-[#1C3B9B]/40 hover:bg-white hover:text-[#1C3B9B] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronsDown className="h-3.5 w-3.5" aria-hidden />
                  باز کردن همه
                </button>
                <button
                  type="button"
                  onClick={handleCollapseAll}
                  className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 text-[11px] font-bold text-slate-700 transition hover:border-[#7c3aed]/40 hover:bg-white hover:text-[#7c3aed]"
                >
                  <ChevronsUp className="h-3.5 w-3.5" aria-hidden />
                  بستن همه
                </button>
              </div>
            </div>

            <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-2 sm:p-2.5">
              <div
                className="pointer-events-none absolute inset-y-2 right-0 z-10 w-7 rounded-r-xl bg-gradient-to-l from-slate-50 to-transparent sm:hidden"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute inset-y-2 left-0 z-10 w-7 rounded-l-xl bg-gradient-to-r from-slate-50 to-transparent sm:hidden"
                aria-hidden
              />
              <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:overflow-x-visible sm:pb-0 [&::-webkit-scrollbar]:hidden">
                <button
                  type="button"
                  onClick={() => handleCategoryClick('all')}
                  className={`snap-center shrink-0 rounded-full border px-3.5 py-2 text-xs font-bold transition sm:text-sm ${
                    activeCategory === 'all'
                      ? 'border-[#1C3B9B] bg-[#1C3B9B] text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#1C3B9B]/45 hover:text-[#1C3B9B]'
                  }`}
                >
                  همه ({FAQ_ITEMS.length})
                </button>
                {FAQ_CATEGORIES.map((cat) => {
                  const Icon = CATEGORY_ICON[cat.id];
                  const n = countsByCategory.get(cat.id) ?? 0;
                  const active = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`inline-flex snap-center shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-bold transition sm:text-sm ${
                        active
                          ? 'border-[#6d28d9] bg-[#7c3aed] text-white shadow-sm'
                          : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#7c3aed]/50 hover:text-[#7c3aed]'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 opacity-90" aria-hidden />
                      <span>{cat.shortLabel}</span>
                      <span className="tabular-nums opacity-85">({n})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div ref={listAnchorRef} className="scroll-mt-[calc(var(--site-header-height,88px)+120px)]" />

        <div className="mt-6 flex flex-col items-stretch justify-between gap-3 border-b border-slate-200/80 pb-4 sm:flex-row sm:items-end">
          <div>
            <h2 className="text-lg font-black text-[#0f172a] sm:text-xl">فهرست پرسش‌ها</h2>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">
              {filteredItems.length === FAQ_ITEMS.length && activeCategory === 'all' && !normalizedDeferred
                ? `${FAQ_ITEMS.length} پرسش در ${FAQ_CATEGORIES.length} دسته — روی هر مورد ضربه بزنید یا لینکش را کپی کنید.`
                : `${filteredItems.length} نتیجه مطابق فیلتر فعلی`}
            </p>
          </div>
          {normalizedDeferred.length >= 2 ? (
            <span className="self-center rounded-full bg-[#1C3B9B]/10 px-3 py-1 text-[11px] font-bold text-[#1C3B9B] sm:self-auto">
              فیلتر متنی فعال
            </span>
          ) : null}
        </div>

        <div className="mt-5 space-y-3">
          <AnimatePresence initial={false}>
            {filteredItems.map((item) => {
              const cat = FAQ_CATEGORIES.find((c) => c.id === item.categoryId)!;
              const Icon = CATEGORY_ICON[item.categoryId];
              const isOpen = isItemOpen(item.id);
              const panelId = `faq-panel-${item.id}`;
              return (
                <motion.article
                  id={`faq-${item.id}`}
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className={`overflow-hidden rounded-2xl border bg-white shadow-[0_14px_36px_-28px_rgba(15,23,42,0.38)] transition-[border-color,box-shadow] ${
                    isOpen
                      ? 'border-[#1C3B9B]/35 ring-1 ring-[#1C3B9B]/10'
                      : 'border-slate-200/90 hover:border-slate-300/95 hover:shadow-[0_18px_40px_-30px_rgba(15,23,42,0.42)]'
                  }`}
                >
                  <div className="flex gap-0">
                    <div
                      className={`w-1 shrink-0 transition-colors duration-300 ${isOpen ? 'bg-gradient-to-b from-[#1C3B9B] to-[#7c3aed]' : 'bg-transparent'}`}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-stretch">
                        <button
                          id={`faq-trigger-${item.id}`}
                          type="button"
                          onClick={() => toggleOpen(item.id)}
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          className="flex min-w-0 flex-1 items-start gap-3 p-4 text-right transition hover:bg-slate-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1C3B9B]/25 sm:gap-4 sm:p-5"
                        >
                          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1C3B9B]/11 to-[#7c3aed]/11 text-[#1C3B9B] ring-1 ring-slate-200/60">
                            <Icon className="h-5 w-5" aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1 text-right">
                            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 ring-1 ring-slate-200/60">
                              {cat.label}
                            </span>
                            <span className="block text-sm font-black leading-7 text-[#0f172a] sm:text-base sm:leading-8">{item.question}</span>
                          </span>
                          <ChevronDown
                            className={`mt-1.5 h-5 w-5 shrink-0 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#1C3B9B]' : ''}`}
                            aria-hidden
                          />
                        </button>
                        <div className="flex shrink-0 flex-col justify-center border-r border-slate-100 py-2 pr-2 pl-1 sm:pr-3 sm:pl-2">
                          <button
                            type="button"
                            onClick={() => copyItemLink(item.id)}
                            className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-[#1C3B9B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C3B9B]/30"
                            title="کپی لینک این سوال"
                            aria-label="کپی لینک این سوال"
                          >
                            {copiedId === item.id ? (
                              <Check className="h-4 w-4 text-emerald-600" aria-hidden />
                            ) : (
                              <Copy className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                        </div>
                      </div>
                      <AnimatePresence initial={false}>
                        {isOpen ? (
                          <motion.div
                            id={panelId}
                            role="region"
                            aria-labelledby={`faq-trigger-${item.id}`}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="border-t border-slate-100/95"
                          >
                            <div className="px-4 pb-4 pt-2 sm:px-5 sm:pb-5 sm:pt-3">
                              <p className="max-w-prose pr-0.5 text-sm leading-8 text-slate-600 sm:text-[15px] sm:leading-[1.85]">{item.answer}</p>
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>

        {filteredItems.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white/85 p-8 text-center shadow-inner">
            <p className="text-base font-black text-slate-800">نتیجه‌ای پیدا نشد</p>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              عبارت را کوتاه‌تر کنید، دستهٔ دیگری را امتحان کنید، یا با پشتیبانی تماس بگیرید.
            </p>
            <button
              type="button"
              onClick={() => {
                setRawQuery('');
                setActiveCategory('all');
                setExpandAll(false);
              }}
              className="mt-5 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-[#1C3B9B] shadow-sm transition hover:bg-slate-50"
            >
              بازنشانی فیلترها
            </button>
          </div>
        ) : null}

        <section className="mt-14 rounded-[1.35rem] border border-slate-200/95 bg-white/95 p-6 text-right shadow-[0_12px_32px_-24px_rgba(15,23,42,0.2)] sm:p-8">
          <h2 className="text-lg font-black text-[#0f172a] sm:text-xl">راهنمای دسته‌ها</h2>
          <p className="mt-2 text-xs text-slate-500 sm:text-sm">برای فیلتر سریع روی هر کارت کلیک کنید.</p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FAQ_CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICON[c.id];
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => handleCategoryClick(c.id)}
                    className="flex h-full w-full gap-3 rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-4 text-right transition hover:border-[#7c3aed]/30 hover:shadow-md"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#7c3aed]/10 text-[#7c3aed]">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <p className="text-sm font-black text-[#0f172a]">{c.label}</p>
                      <p className="mt-1 text-xs leading-6 text-slate-600">{c.description}</p>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <AnimatePresence>
        {showScrollTop ? (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.2 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 end-6 z-40 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/30 bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] text-white shadow-[0_14px_28px_-8px_rgba(28,59,155,0.55)] transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C3B9B]/40 active:scale-95"
            aria-label="بازگشت به بالای صفحه"
          >
            <ArrowUp className="h-5 w-5" aria-hidden />
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default FAQPage;
